/**
 * Staff session for the internal admin console. CVSI staff sign in with a staff token and identify
 * themselves (operator email) so their actions are attributed in the audit trail. Production upgrade:
 * enterprise SSO (OIDC) + MFA — the token here is replaced by an SSO-minted session token.
 */

import { normalizeCvsiError, CvsiError, type StaffRole } from '@cvsi/shared';

const KEY = 'cvsi.admin.session';

export interface StaffSession {
  baseUrl: string;
  token: string;
  actorId: string;
}

export interface StaffLoginResult {
  token: string;
  actorId: string;
  email: string;
  role: StaffRole;
  areas: string[];
  expiresInSeconds: number;
}

export function loadSession(): StaffSession | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as StaffSession) : null;
  } catch {
    return null;
  }
}

export function saveSession(s: StaffSession): void {
  sessionStorage.setItem(KEY, JSON.stringify(s));
}

export function clearSession(): void {
  sessionStorage.removeItem(KEY);
}

export function defaultBaseUrl(): string {
  return (import.meta.env.VITE_CVSI_INTERNAL_BASE_URL as string) || 'https://cvsiinternal-3qqpinimfq-uc.a.run.app';
}

export async function loginWithPassword(baseUrl: string, body: { email: string; password: string }): Promise<StaffLoginResult> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl.replace(/\/$/, '')}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new CvsiError('network', 0, err instanceof Error ? err.message : 'Network request failed');
  }
  if (!res.ok) throw await normalizeCvsiError(res);
  return (await res.json()) as StaffLoginResult;
}

/** Google Client ID used for the staff "Sign in with Google" button, or null if not configured. */
export function googleClientId(): string | null {
  return (import.meta.env.VITE_GOOGLE_CLIENT_ID as string) || null;
}

export async function loadAuthConfig(baseUrl: string): Promise<{ googleClientId: string | null }> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl.replace(/\/$/, '')}/auth/config`);
  } catch {
    return { googleClientId: googleClientId() };
  }
  if (!res.ok) return { googleClientId: googleClientId() };
  const body = (await res.json()) as { googleClientId?: unknown };
  return { googleClientId: typeof body.googleClientId === 'string' && body.googleClientId ? body.googleClientId : googleClientId() };
}

/** Exchange a Google ID token (from the Google Identity Services button) for a staff session. */
export async function loginWithGoogle(baseUrl: string, idToken: string): Promise<StaffLoginResult> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl.replace(/\/$/, '')}/auth/login/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idToken }),
    });
  } catch (err) {
    throw new CvsiError('network', 0, err instanceof Error ? err.message : 'Network request failed');
  }
  if (!res.ok) throw await normalizeCvsiError(res);
  return (await res.json()) as StaffLoginResult;
}
